/**
 * Calls a Microsoft Graph API and returns the response.
 *
 * @param accessToken The access token to use for the request.
 * @param path Path component of the URI, e.g., "/me". Should start with "/".
 * @param method The HTTP method (e.g., "GET", "POST", "PUT", "PATCH", "DELETE").
 * @param queryParams Optional query parameters, e.g., "?$select=name,id". Should start with "?" if provided.
 * @param body Optional request body. If an object, it will be JSON.stringified and Content-Type set to application/json unless specified in additionalHeaders.
 * @param additionalHeaders Optional additional headers to include in the request (e.g., { "Content-Type": "application/json" }).
 * @returns A promise that resolves with the JSON response from Microsoft Graph.
 */
export async function makeGraphRequest(
  accessToken: string,
  path: string,
  method: string,
  queryParams?: string,
  body?: any,
  additionalHeaders?: Record<string, string>
): Promise<any> {
  if (!path) throw new Error("path is required.");
  if (!path.startsWith("/")) throw new Error("path must start with '/'.");
  if (!method) throw new Error("method is required.");
  if (queryParams && !queryParams.startsWith("?")) throw new Error("queryParams must start with '?'.");

  const headers: Record<string, string> = {
    ...additionalHeaders, // Spread additional headers first
    Authorization: `Bearer ${accessToken}`, // Set/override Authorization with Bearer token
  };

  const requestOptions: any = {
    method: method,
    headers: headers,
  };

  if (body !== undefined) {
    if (typeof body === "object" && body !== null) {
      requestOptions.body = JSON.stringify(body);
      // Set Content-Type to application/json if body is an object and Content-Type is not already set
      const contentTypeKey = Object.keys(headers).find((key) => key.toLowerCase() === "content-type");
      if (!contentTypeKey) {
        headers["Content-Type"] = "application/json";
      }
    } else {
      requestOptions.body = body as any; // For string, FormData, etc.
    }
  }

  const fullUrl = `https://graph.microsoft.com/v1.0${path}${queryParams || ""}`;
  const response = await fetch(fullUrl, requestOptions);

  if (response.ok) {
    const data = await response.json();
    console.log("Graph API Response:", data); // Kept original console.log
    return data;
  } else {
    throw new Error(response.statusText);
  }
}

/**
 * Converts an EWS Item ID to a Graph REST Immutable ID.
 * @param accessToken The access token for Microsoft Graph.
 * @param ewsItemId The EWS Item ID to convert.
 * @returns A promise that resolves with the Graph REST Immutable ID, or undefined if conversion fails.
 */
export async function getGraphRestIdFromEwsId(accessToken: string, ewsItemId: string): Promise<string | undefined> {
  if (!ewsItemId) {
    console.warn("EWS Item ID is missing, cannot convert to Graph REST ID.");
    return undefined;
  }
  try {
    const path = "/me/translateExchangeIds";
    const body = {
      InputIds: [ewsItemId],
      TargetIdType: "RestImmutableEntryId",
      SourceIdType: "EwsId",
    };

    const response = await makeGraphRequest(accessToken, path, "POST", undefined, body, {
      "Content-type": "application/json",
    });

    if (response && response.value && response.value.length > 0 && response.value[0].targetId) {
      console.log("Successfully converted EWS ID to Graph REST ID:", response.value[0].targetId);
      return response.value[0].targetId;
    } else {
      console.warn("Could not extract targetId from translateExchangeIds response:", response);
      return undefined;
    }
  } catch (error) {
    console.error("Error calling translateExchangeIds Graph API:", error);
    return undefined;
  }
}

/**
 * Deletes specific categories from a message using Microsoft Graph API.
 * It first retrieves the current categories, removes the specified ones,
 * and then updates the message with the remaining categories.
 * @param accessToken The access token for Microsoft Graph.
 * @param graphRestId The Graph REST ID of the message.
 * @param categoriesToDelete An array of category strings to delete from the message.
 * @returns A promise that resolves to `true` if the operation was successful, `false` otherwise.
 */
export async function deleteMessageCategoriesViaGraph(
  accessToken: string,
  graphRestId: string,
  categoriesToDelete: string[]
): Promise<boolean> {
  if (!graphRestId) {
    console.error("Graph REST ID is missing, cannot delete categories.");
    return false;
  }
  if (!Array.isArray(categoriesToDelete)) {
    console.error("Categories to delete input must be an array.");
    return false;
  }
  if (categoriesToDelete.length === 0) {
    console.log("No categories specified for deletion. No action needed.");
    return true; // Consider it successful if there's nothing to delete
  }

  console.log("Categories to delete:", categoriesToDelete);

  // 1. Get the current categories
  const currentCategories = await getMessageCategoriesViaGraph(accessToken, graphRestId);

  if (currentCategories === undefined) {
    console.error("Failed to retrieve current categories for deletion.");
    return false; // Failed to get current categories
  }

  // 2. Filter out the categories to delete
  const categoriesToKeep = currentCategories.filter((cat) => !categoriesToDelete.includes(cat));
  console.log("Categories to keep:", categoriesToKeep);

  // Optimization: If no categories were removed, there's no need to make an API call.
  if (categoriesToKeep.length === currentCategories.length) {
    console.log("No matching categories found to delete. No update needed.");
    return true;
  }

  // 3. Update the message with the remaining categories
  return setMessageCategoriesViaGraph(accessToken, graphRestId, categoriesToKeep);
}

/**
 * Sets the categories for a given message using Microsoft Graph API and validates the response.
 * @param accessToken The access token for Microsoft Graph.
 * @param graphRestId The Graph REST ID of the message to update.
 * @param categoriesToSet An array of category strings to set on the message.
 * @returns A promise that resolves to `true` if categories were successfully set and validated, `false` otherwise.
 */
export async function setMessageCategoriesViaGraph(
  accessToken: string,
  graphRestId: string,
  categoriesToSet: string[]
): Promise<boolean> {
  if (!graphRestId) {
    console.error("Graph REST ID is missing, cannot set categories.");
    return false;
  }
  if (categoriesToSet === null || categoriesToSet === undefined) {
    console.error("Categories to set input is null or undefined.");
    return false; // Invalid input
  }

  console.log("Categories to set:", categoriesToSet);

  const path = `/me/messages/${graphRestId}`;
  const body = {
    Categories: categoriesToSet,
  };

  try {
    const response = await makeGraphRequest(accessToken, path, "PATCH", undefined, body, {
      "Content-type": "application/json",
    });

    if (response && Array.isArray(response.categories)) {
      const returnedCategories: string[] = response.categories;
      // Validate if the returned categories match the ones we intended to set.
      // Check length and content equality. Using `every` with `includes` handles order differences.
      // This works for both setting categories (non-empty array) and clearing them (empty array).
      const setCategoriesMatch =
        returnedCategories.length === categoriesToSet.length &&
        categoriesToSet.every((cat) => returnedCategories.includes(cat));

      if (setCategoriesMatch) {
        console.log(`Successfully set and validated categories for message ${graphRestId}:`, returnedCategories);
        return true;
      } else {
        console.warn(
          `Categories set for message ${graphRestId}, but validation failed. Expected: ${categoriesToSet}, Got: ${returnedCategories}`
        );
        return false;
      }
    } else {
      console.error(`Failed to set categories or invalid response structure for message ${graphRestId}:`, response);
      return false;
    }
  } catch (error) {
    console.error(`Error setting categories for message ${graphRestId} via Graph API:`, error);
    return false;
  }
}

/**
 * Retrieves the categories for a given message using Microsoft Graph API.
 * @param accessToken The access token for Microsoft Graph.
 * @param graphRestId The Graph REST ID of the message.
 * @returns A promise that resolves to an array of category strings,
 *          an empty array if no categories are set, or undefined if an error occurs.
 */
export async function getMessageCategoriesViaGraph(
  accessToken: string,
  graphRestId: string
): Promise<string[] | undefined> {
  if (!graphRestId) {
    console.error("Graph REST ID is missing, cannot get categories.");
    return undefined;
  }

  const path = `/me/messages/${graphRestId}`;
  const queryParams = "?$select=categories";

  try {
    const response = await makeGraphRequest(accessToken, path, "GET", queryParams);
    if (response && Array.isArray(response.categories)) {
      console.log(`Successfully retrieved categories for message ${graphRestId}:`, response.categories);
      return response.categories;
    }
    return []; // Return empty array if categories property is not an array or missing but response was OK
  } catch (error) {
    console.error(`Error getting categories for message ${graphRestId} via Graph API:`, error);
    return undefined;
  }
}

/**
 * Appends a given list of categories to the existing categories of a message using Microsoft Graph API.
 * It first retrieves the current categories, adds the unique new ones,
 * and then updates the message with the combined list.
 * @param accessToken The access token for Microsoft Graph.
 * @param graphRestId The Graph REST ID of the message.
 * @param categoriesToAppend An array of category strings to append to the message.
 * @returns A promise that resolves to `true` if the categories were successfully appended and validated, `false` otherwise.
 */
export async function appendMessageCategoriesViaGraph(
  accessToken: string,
  graphRestId: string,
  categoriesToAppend: string[]
): Promise<boolean> {
  if (!graphRestId) {
    console.error("Graph REST ID is missing, cannot append categories.");
    return false;
  }
  if (!Array.isArray(categoriesToAppend)) {
    console.error("Categories to append input must be an array.");
    return false;
  }
  if (categoriesToAppend.length === 0) {
    console.log("No categories specified to append. No action needed.");
    return true; // Consider it successful if there's nothing to append
  }

  // 1. Get the current categories
  const currentCategories = await getMessageCategoriesViaGraph(accessToken, graphRestId);

  if (currentCategories === undefined) {
    console.error("Failed to retrieve current categories for appending.");
    return false; // Failed to get current categories
  }

  console.log("Current categories:", currentCategories);
  console.log("Categories to append:", categoriesToAppend);

  // 2. Combine current and new categories, ensuring uniqueness
  const combinedCategories = Array.from(new Set([...currentCategories, ...categoriesToAppend]));
  console.log("Combined categories:", combinedCategories);

  // 3. Set the combined unique categories back to the message
  // This will effectively append new categories while keeping existing ones.
  return setMessageCategoriesViaGraph(accessToken, graphRestId, combinedCategories);
}
